import React, { forwardRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { MultiUserPickerInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-multi-user-picker-inline-edit-full/src/ui/multi-user-picker/index.tsx';
import type { multiUserPicker_nativeIssueTable_MultiUserPickerCell$key } from '@atlassian/jira-relay/src/__generated__/multiUserPicker_nativeIssueTable_MultiUserPickerCell.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';

export type Props = {
	fieldRef: multiUserPicker_nativeIssueTable_MultiUserPickerCell$key;
	isFirstField?: boolean;
};

export const MultiUserPickerCell = ({ fieldRef, isFirstField }: Props) => {
	const data = useFragment<multiUserPicker_nativeIssueTable_MultiUserPickerCell$key>(
		graphql`
			fragment multiUserPicker_nativeIssueTable_MultiUserPickerCell on JiraMultipleSelectUserPickerField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...multiUserPicker_issueFieldMultiUserPickerInlineEditFull_MultiUserPickerInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;

	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

	useLazyLoadEditability(data.id, isEditable);

	if (isSingleLineRowHeightEnabled && fg('single_line_row_height_collapsable_fields')) {
		return (
			<MultiUserPickerInlineEditViewWithIsEditable
				attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
				spacing="default"
				editViewPopup
				editViewPopupAlignBlock={isFirstField ? 'start' : 'center'}
				editViewPopupTarget={isFirstField ? CustomTarget : undefined}
				fragmentRef={data}
				isEditable={isEditable ?? false}
				menuPosition={isFirstField ? 'absolute' : 'fixed'}
				readViewFitContainerHeight
			/>
		);
	}

	return (
		<MultiUserPickerInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			menuPosition="fixed"
			isEditable={isEditable ?? false}
			readViewFitContainerHeight
		/>
	);
};

const CustomTarget = forwardRef<HTMLDivElement, {}>((_, ref) => {
	return <Box xcss={customTargetStyles} ref={ref} />;
});

const customTargetStyles = xcss({
	width: 'space.0',
	position: 'absolute',
	top: 'space.0',
});
